import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  MapPin, 
  Clock, 
  InstagramLogo, 
  FacebookLogo, 
  Envelope,
  List,
  X,
  ArrowRight,
  CalendarBlank,
  Sparkle,
  Heart
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#gallery", label: "Galleria" },
    { href: "#services", label: "Hinnasto" },
    { href: "#contact", label: "Yhteystiedot" },
    { href: "#booking", label: "Varaa aika" }
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/90 backdrop-blur-xl border-b border-border/50 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/dkte08z4_photo_2025-03-11_15-36-13.jpg" 
              alt="Jalkahoitola Seniora Logo" 
              className="w-12 h-12 object-contain rounded-lg"
            />
            <span className="font-serif text-2xl font-light tracking-tight text-burgundy">
              Jalkahoitola Seniora
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-sm font-sans tracking-wide text-foreground/80 hover:text-foreground transition-colors"
                data-testid={`nav-${link.href.replace("#", "")}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-4 px-6 gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left py-2 text-foreground/80 hover:text-foreground transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?w=1920&q=80"
          alt="Spa treatment"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="flex flex-col lg:flex-row items-center gap-12"
        >
          <div className="flex-1 max-w-2xl">
            <motion.p
              variants={fadeInUp}
              className="text-sm font-sans tracking-[0.2em] uppercase text-muted-foreground mb-6"
            >
              Tervetuloa
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-serif font-light tracking-tight leading-tight mb-6"
            >
              Jalkahoitola
              <br />
              <span className="text-burgundy">Seniora</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-lg font-sans font-light leading-relaxed text-foreground/80 mb-10"
            >
              Ammattitaitoista jalkahoitoa rauhallisessa ja rentouttavassa
              ympäristössä. Huolehditaan yhdessä jalkojesi hyvinvoinnista.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <button
                onClick={() => document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-8 py-4 bg-burgundy text-white rounded-full font-sans text-sm tracking-wide hover:bg-burgundy-dark transition-colors duration-300"
                data-testid="hero-cta-btn"
              >
                Varaa aika
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-8 py-4 border border-border rounded-full font-sans text-sm tracking-wide hover:bg-secondary transition-colors duration-300"
                data-testid="hero-services-btn"
              >
                Katso hinnasto
              </button>
            </motion.div>
          </div>
          
          {/* Master Photo */}
          <motion.div
            variants={fadeInUp}
            className="flex-shrink-0"
          >
            <div className="relative">
              <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/h5uw3zph_photo_2026-03-02_18-48-31.jpg"
                  alt="Jalkahoitaja"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-2 rounded-full shadow-lg">
                <p className="font-serif text-burgundy text-sm whitespace-nowrap">Ammattitaitoinen hoitaja</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = ({ photos }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p
            variants={fadeInUp}
            className="text-sm font-sans tracking-[0.15em] uppercase text-muted-foreground mb-4"
          >
            Portfolio
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light tracking-tight mb-12"
          >
            Töitämme
          </motion.h2>

          {/* Bento Grid */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                className={`relative overflow-hidden rounded-2xl cursor-pointer group ${
                  index === 0 ? "col-span-2 row-span-2" : ""
                }`}
                onClick={() => setSelectedPhoto(photo)}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                data-testid={`gallery-photo-${index}`}
              >
                <div className={`${index === 0 ? "aspect-square" : "aspect-square"}`}>
                  <img
                    src={photo.url}
                    alt={photo.title || "Jalkahoito"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-sans text-sm">{photo.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-foreground/90 flex items-center justify-center p-6"
            onClick={() => setSelectedPhoto(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white p-2"
              onClick={() => setSelectedPhoto(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedPhoto.url}
              alt={selectedPhoto.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Services Section
const ServicesSection = ({ services }) => {
  return (
    <section id="services" data-testid="services-section" className="py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p
            variants={fadeInUp}
            className="text-sm font-sans tracking-[0.15em] uppercase text-muted-foreground mb-4"
          >
            Palvelut
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light tracking-tight mb-12"
          >
            Hinnasto
          </motion.h2>

          <motion.div variants={fadeInUp} className="space-y-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md transition-shadow duration-300"
                data-testid={`service-${index}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-serif font-normal mb-2">{service.name}</h3>
                    <p className="text-muted-foreground font-sans font-light text-sm">
                      {service.description}
                    </p>
                    {service.duration && (
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Clock size={14} />
                        {service.duration}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-serif font-light">{service.price} €</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ contact }) => {
  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p
            variants={fadeInUp}
            className="text-sm font-sans tracking-[0.15em] uppercase text-muted-foreground mb-4"
          >
            Yhteystiedot
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light tracking-tight mb-12"
          >
            Ota yhteyttä
          </motion.h2>

          <motion.div
            variants={fadeInUp}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {/* Phone */}
            <a
              href={`tel:${contact.phone?.replace(/\s/g, "")}`}
              className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md transition-all duration-300 group"
              data-testid="contact-phone"
            >
              <Phone size={32} className="text-accent mb-4" weight="duotone" />
              <h3 className="font-serif text-lg mb-2">Puhelin</h3>
              <p className="text-muted-foreground font-sans font-light group-hover:text-foreground transition-colors">
                {contact.phone}
              </p>
            </a>

            {/* Address */}
            <div
              className="bg-card border border-border/50 rounded-2xl p-6"
              data-testid="contact-address"
            >
              <MapPin size={32} className="text-accent mb-4" weight="duotone" />
              <h3 className="font-serif text-lg mb-2">Osoite</h3>
              <p className="text-muted-foreground font-sans font-light">{contact.address}</p>
            </div>

            {/* Working Hours */}
            <div
              className="bg-card border border-border/50 rounded-2xl p-6"
              data-testid="contact-hours"
            >
              <Clock size={32} className="text-accent mb-4" weight="duotone" />
              <h3 className="font-serif text-lg mb-2">Aukioloajat</h3>
              <p className="text-muted-foreground font-sans font-light">{contact.working_hours}</p>
            </div>

            {/* Email */}
            <a
              href={`mailto:${contact.email}`}
              className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md transition-all duration-300 group"
              data-testid="contact-email"
            >
              <Envelope size={32} className="text-accent mb-4" weight="duotone" />
              <h3 className="font-serif text-lg mb-2">Sähköposti</h3>
              <p className="text-muted-foreground font-sans font-light group-hover:text-foreground transition-colors">
                {contact.email}
              </p>
            </a>

            {/* Social Media */}
            <div className="bg-card border border-border/50 rounded-2xl p-6 md:col-span-2 lg:col-span-2">
              <Heart size={32} className="text-accent mb-4" weight="duotone" />
              <h3 className="font-serif text-lg mb-4">Seuraa meitä</h3>
              <div className="flex gap-4">
                {contact.instagram && (
                  <a
                    href={contact.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full hover:bg-secondary transition-colors"
                    data-testid="social-instagram"
                  >
                    <InstagramLogo size={20} />
                    Instagram
                  </a>
                )}
                {contact.facebook && (
                  <a
                    href={contact.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full hover:bg-secondary transition-colors"
                    data-testid="social-facebook"
                  >
                    <FacebookLogo size={20} />
                    Facebook
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = ({ services }) => {
  const [formData, setFormData] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/appointments`, formData);
      setSubmitStatus("success");
      setFormData({
        client_name: "",
        client_phone: "",
        client_email: "",
        service: "",
        preferred_date: "",
        preferred_time: "",
        message: ""
      });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-2xl mx-auto"
        >
          <motion.p
            variants={fadeInUp}
            className="text-sm font-sans tracking-[0.15em] uppercase text-muted-foreground mb-4 text-center"
          >
            Ajanvaraus
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light tracking-tight mb-4 text-center"
          >
            Varaa aikasi
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-muted-foreground font-sans font-light text-center mb-12"
          >
            Täytä alla oleva lomake ja otamme sinuun yhteyttä vahvistaaksemme aikasi.
          </motion.p>

          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-sans tracking-wide mb-2">Nimi *</label>
                <input
                  type="text"
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                  placeholder="Etunimi Sukunimi"
                  data-testid="booking-name"
                />
              </div>
              <div>
                <label className="block text-sm font-sans tracking-wide mb-2">Puhelin *</label>
                <input
                  type="tel"
                  required
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                  placeholder="+358 40 123 4567"
                  data-testid="booking-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-sans tracking-wide mb-2">Sähköposti</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                placeholder="email@example.com"
                data-testid="booking-email"
              />
            </div>

            <div>
              <label className="block text-sm font-sans tracking-wide mb-2">Palvelu *</label>
              <select
                required
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                data-testid="booking-service"
              >
                <option value="">Valitse palvelu</option>
                {services.map((service) => (
                  <option key={service.id} value={service.name}>
                    {`${service.name} - ${service.price} €`}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-sans tracking-wide mb-2">Toivottu päivä *</label>
                <input
                  type="date"
                  required
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                  data-testid="booking-date"
                />
              </div>
              <div>
                <label className="block text-sm font-sans tracking-wide mb-2">Toivottu aika *</label>
                <input
                  type="time"
                  required
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans"
                  data-testid="booking-time"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-sans tracking-wide mb-2">Viesti</label>
              <textarea
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary font-sans resize-none"
                placeholder="Lisätietoja tai toiveita..."
                data-testid="booking-message"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-sans text-sm tracking-wide hover:bg-accent transition-colors duration-300 disabled:opacity-50"
              data-testid="booking-submit"
            >
              {isSubmitting ? (
                "Lähetetään..."
              ) : (
                <>
                  <CalendarBlank size={18} />
                  Lähetä ajanvaraus
                </>
              )}
            </button>

            {submitStatus === "success" && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-green-600 font-sans"
                data-testid="booking-success"
              >
                Kiitos! Ajanvaraus vastaanotettu. Otamme sinuun yhteyttä pian.
              </motion.p>
            )}

            {submitStatus === "error" && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-red-600 font-sans"
                data-testid="booking-error"
              >
                Virhe lähettäessä. Yritä uudelleen tai soita meille.
              </motion.p>
            )}
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/dkte08z4_photo_2025-03-11_15-36-13.jpg" 
              alt="Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-serif text-lg text-burgundy">Jalkahoitola Seniora</span>
          </div>
          <p className="text-sm text-muted-foreground font-sans">
            © {new Date().getFullYear()} Jalkahoitola Seniora. Kaikki oikeudet pidätetään.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => {
  const [photos, setPhotos] = useState([]);
  const [services, setServices] = useState([]);
  const [contact, setContact] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [photosRes, servicesRes, contactRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/services`),
          axios.get(`${API}/contact`)
        ]);
        setPhotos(photosRes.data);
        setServices(servicesRes.data);
        setContact(contactRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/dkte08z4_photo_2025-03-11_15-36-13.jpg" 
            alt="Logo" 
            className="w-16 h-16 mx-auto mb-4 animate-pulse"
          />
          <p className="font-sans text-muted-foreground">Ladataan...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <Header />
      <main>
        <HeroSection />
        <GallerySection photos={photos} />
        <ServicesSection services={services} />
        <ContactSection contact={contact} />
        <BookingSection services={services} />
      </main>
      <Footer />
    </div>
  );
};

// Admin Page Component - to be created in separate file
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [activeTab, setActiveTab] = useState("gallery");
  const [photos, setPhotos] = useState([]);
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contact, setContact] = useState({});
  const [authHeader, setAuthHeader] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    const header = "Basic " + btoa(`${credentials.username}:${credentials.password}`);
    try {
      await axios.get(`${API}/admin/appointments`, {
        headers: { Authorization: header }
      });
      setAuthHeader(header);
      setIsAuthenticated(true);
      fetchAllData(header);
    } catch (error) {
      alert("Virheelliset tunnukset");
    }
  };

  const fetchAllData = async (header) => {
    try {
      const [photosRes, servicesRes, appointmentsRes, contactRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/appointments`, { headers: { Authorization: header } }),
        axios.get(`${API}/contact`)
      ]);
      setPhotos(photosRes.data);
      setServices(servicesRes.data);
      setAppointments(appointmentsRes.data);
      setContact(contactRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Gallery Management
  const [newPhoto, setNewPhoto] = useState({ url: "", title: "", description: "", order: 0 });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 
          Authorization: authHeader,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Add photo to gallery with uploaded URL
      const photoData = {
        url: uploadRes.data.url,
        title: file.name.replace(/\.[^/.]+$/, ""),
        description: "",
        order: photos.length + 1
      };
      
      const res = await axios.post(`${API}/admin/gallery`, photoData, {
        headers: { Authorization: authHeader }
      });
      setPhotos([...photos, res.data]);
    } catch (error) {
      alert("Virhe ladattaessa kuvaa: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const addPhoto = async () => {
    if (!newPhoto.url) return;
    try {
      const res = await axios.post(`${API}/admin/gallery`, newPhoto, {
        headers: { Authorization: authHeader }
      });
      setPhotos([...photos, res.data]);
      setNewPhoto({ url: "", title: "", description: "", order: photos.length + 1 });
    } catch (error) {
      alert("Virhe lisättäessä kuvaa");
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Haluatko varmasti poistaa kuvan?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { Authorization: authHeader }
      });
      setPhotos(photos.filter((p) => p.id !== id));
    } catch (error) {
      alert("Virhe poistettaessa kuvaa");
    }
  };

  // Services Management
  const [newService, setNewService] = useState({ name: "", description: "", price: 0, duration: "", order: 0 });

  const addService = async () => {
    if (!newService.name) return;
    try {
      const res = await axios.post(`${API}/admin/services`, newService, {
        headers: { Authorization: authHeader }
      });
      setServices([...services, res.data]);
      setNewService({ name: "", description: "", price: 0, duration: "", order: services.length + 1 });
    } catch (error) {
      alert("Virhe lisättäessä palvelua");
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Haluatko varmasti poistaa palvelun?")) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, {
        headers: { Authorization: authHeader }
      });
      setServices(services.filter((s) => s.id !== id));
    } catch (error) {
      alert("Virhe poistettaessa palvelua");
    }
  };

  // Contact Management
  const updateContact = async () => {
    try {
      await axios.put(`${API}/admin/contact`, contact, {
        headers: { Authorization: authHeader }
      });
      alert("Yhteystiedot päivitetty!");
    } catch (error) {
      alert("Virhe päivitettäessä yhteystietoja");
    }
  };

  // Appointment Management
  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}/status?status=${status}`, {}, {
        headers: { Authorization: authHeader }
      });
      setAppointments(appointments.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (error) {
      alert("Virhe päivitettäessä tilaa");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/dkte08z4_photo_2025-03-11_15-36-13.jpg" 
              alt="Logo" 
              className="w-16 h-16 object-contain mx-auto mb-4"
            />
            <h1 className="text-2xl font-serif text-burgundy">Admin-kirjautuminen</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <input
              type="text"
              placeholder="Käyttäjätunnus"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="admin-username"
            />
            <input
              type="password"
              placeholder="Salasana"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="admin-password"
            />
            <button
              type="submit"
              className="w-full px-4 py-3 bg-burgundy text-white rounded-full hover:bg-burgundy-dark transition-colors"
              data-testid="admin-login-btn"
            >
              Kirjaudu
            </button>
          </form>
          <Link to="/" className="block text-center mt-6 text-muted-foreground hover:text-foreground">
            ← Takaisin etusivulle
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_chat-start-117/artifacts/dkte08z4_photo_2025-03-11_15-36-13.jpg" 
              alt="Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-serif text-xl text-burgundy">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              Näytä sivusto
            </Link>
            <button
              onClick={() => setIsAuthenticated(false)}
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="admin-logout"
            >
              Kirjaudu ulos
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: "gallery", label: "Galleria" },
            { id: "services", label: "Palvelut" },
            { id: "contact", label: "Yhteystiedot" },
            { id: "appointments", label: "Varaukset" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-full text-sm font-sans whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-accent"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div data-testid="admin-gallery">
            <h2 className="text-2xl font-serif mb-6">Gallerian hallinta</h2>

            {/* Drag & Drop Upload Area */}
            <div 
              className={`bg-card border-2 border-dashed rounded-2xl p-8 mb-6 text-center transition-colors ${
                dragActive ? "border-burgundy bg-burgundy/5" : "border-border"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
                data-testid="file-upload-input"
              />
              <label 
                htmlFor="file-upload" 
                className="cursor-pointer"
              >
                <div className="flex flex-col items-center gap-4">
                  {uploading ? (
                    <div className="w-12 h-12 border-4 border-burgundy border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <p className="font-sans font-medium text-foreground">
                      {uploading ? "Ladataan..." : "Vedä ja pudota kuva tähän"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      tai klikkaa valitaksesi tiedosto (JPEG, PNG, WebP, GIF, max 5MB)
                    </p>
                  </div>
                </div>
              </label>
            </div>

            {/* Alternative: Add via URL */}
            <div className="bg-card border border-border rounded-2xl p-6 mb-6">
              <h3 className="font-sans font-medium mb-4">Tai lisää URL-osoitteella</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Kuvan URL"
                  value={newPhoto.url}
                  onChange={(e) => setNewPhoto({ ...newPhoto, url: e.target.value })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-photo-url"
                />
                <input
                  type="text"
                  placeholder="Otsikko"
                  value={newPhoto.title}
                  onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-photo-title"
                />
              </div>
              <button
                onClick={addPhoto}
                disabled={!newPhoto.url}
                className="px-6 py-2 bg-burgundy text-white rounded-full text-sm hover:bg-burgundy-dark transition-colors disabled:opacity-50"
                data-testid="add-photo-btn"
              >
                Lisää kuva
              </button>
            </div>

            {/* Photos Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group rounded-2xl overflow-hidden">
                  <img src={photo.url} alt={photo.title} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => deletePhoto(photo.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded-full text-sm"
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      Poista
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div data-testid="admin-services">
            <h2 className="text-2xl font-serif mb-6">Palveluiden hallinta</h2>

            {/* Add Service Form */}
            <div className="bg-card border border-border rounded-2xl p-6 mb-6">
              <h3 className="font-sans font-medium mb-4">Lisää uusi palvelu</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Palvelun nimi *"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-service-name"
                />
                <input
                  type="number"
                  placeholder="Hinta (€) *"
                  value={newService.price || ""}
                  onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-service-price"
                />
                <input
                  type="text"
                  placeholder="Kesto (esim. 45 min)"
                  value={newService.duration}
                  onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-service-duration"
                />
                <input
                  type="text"
                  placeholder="Kuvaus"
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  className="px-4 py-2 border border-border rounded-xl"
                  data-testid="add-service-desc"
                />
              </div>
              <button
                onClick={addService}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-full text-sm hover:bg-accent transition-colors"
                data-testid="add-service-btn"
              >
                Lisää palvelu
              </button>
            </div>

            {/* Services List */}
            <div className="space-y-3">
              {services.map((service) => (
                <div key={service.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-serif">{service.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {service.price} € • {service.duration}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteService(service.id)}
                    className="px-4 py-1 text-red-500 border border-red-500 rounded-full text-sm hover:bg-red-50 transition-colors"
                    data-testid={`delete-service-${service.id}`}
                  >
                    Poista
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <div data-testid="admin-contact">
            <h2 className="text-2xl font-serif mb-6">Yhteystietojen hallinta</h2>
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm mb-2">Puhelin</label>
                  <input
                    type="text"
                    value={contact.phone || ""}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Sähköposti</label>
                  <input
                    type="email"
                    value={contact.email || ""}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Osoite</label>
                  <input
                    type="text"
                    value={contact.address || ""}
                    onChange={(e) => setContact({ ...contact, address: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-address-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Aukioloajat</label>
                  <input
                    type="text"
                    value={contact.working_hours || ""}
                    onChange={(e) => setContact({ ...contact, working_hours: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-hours-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Instagram URL</label>
                  <input
                    type="text"
                    value={contact.instagram || ""}
                    onChange={(e) => setContact({ ...contact, instagram: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-instagram-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Facebook URL</label>
                  <input
                    type="text"
                    value={contact.facebook || ""}
                    onChange={(e) => setContact({ ...contact, facebook: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    data-testid="contact-facebook-input"
                  />
                </div>
              </div>
              <button
                onClick={updateContact}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-full text-sm hover:bg-accent transition-colors"
                data-testid="save-contact-btn"
              >
                Tallenna muutokset
              </button>
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === "appointments" && (
          <div data-testid="admin-appointments">
            <h2 className="text-2xl font-serif mb-6">Varausten hallinta</h2>
            {appointments.length === 0 ? (
              <p className="text-muted-foreground">Ei varauksia</p>
            ) : (
              <div className="space-y-4">
                {appointments.map((apt) => (
                  <div key={apt.id} className="bg-card border border-border rounded-2xl p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div>
                        <h4 className="font-serif text-lg">{apt.client_name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">
                          {apt.client_phone} {apt.client_email && `• ${apt.client_email}`}
                        </p>
                        <p className="text-sm">
                          <strong>Palvelu:</strong> {apt.service}
                        </p>
                        <p className="text-sm">
                          <strong>Toivottu aika:</strong> {apt.preferred_date} klo {apt.preferred_time}
                        </p>
                        {apt.message && (
                          <p className="text-sm mt-2 text-muted-foreground">"{apt.message}"</p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={apt.status}
                          onChange={(e) => updateAppointmentStatus(apt.id, e.target.value)}
                          className="px-3 py-1 border border-border rounded-lg text-sm"
                          data-testid={`appointment-status-${apt.id}`}
                        >
                          <option value="pending">Odottaa</option>
                          <option value="confirmed">Vahvistettu</option>
                          <option value="completed">Valmis</option>
                          <option value="cancelled">Peruutettu</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
